<script setup lang="ts">
import { stringToFaDateTime } from '@payjib/shared'
import { useQueryClient } from '@tanstack/vue-query'
import { confirmSellerIranianPaymentAction } from '@/api/functions/transactions/transaction-status.action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'
import { numberWithCommas } from '@/utils/number-with-commas'

/* =========================
   Types
========================= */

interface BuyerPayment {
  amount: number
  provider: string
  transferType: string
  tracking_code: string
  statusUpdatedAt: string
}

/* =========================
   Props
========================= */

const props = withDefaults(
  defineProps<{
    modelValue: boolean
    receipts?: string[]
    uuid: string
    buyerPayment?: BuyerPayment[]
  }>(),
  {
    receipts: () => [],
    buyerPayment: () => [],
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'confirm'): void
  (e: 'cancel'): void
}>()

/* =========================
   Logic
========================= */

const toast = useToast()
const queryClient = useQueryClient()

const confirmMutation = useMutationApi({
  mutationFn: () => confirmSellerIranianPaymentAction(props.uuid),
  onSuccess: () => {
    toast.success('تایید و ثبت پرداخت با موفقیت انجام شد')
    queryClient.invalidateQueries({
      queryKey: ['transactionDetails', props.uuid],
    })
    emit('confirm')
    closeModal()
  },
  onError: (err: unknown) => {
    toast.error(getErrorMessage(err))
  },
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
}

const confirmSave = () => {
  if (confirmMutation.isPending.value) return
  confirmMutation.mutate()
}

const openImageInNewTab = (url: string) => {
  window.open(url, '_blank', 'noopener')
}
</script>

<template>
  <Modal
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    width="600px"
    background="#FFF"
    :closable="true"
    title="تایید نهایی"
    closeDirection="left"
    class="update-request"
  >
    <div class="flex flex-col h-[600px]">
      <div class="flex-1 px-4 py-6 pb-9 space-y-1">
        <div class="flex flex-col gap-8">
          <!-- Receipt Section -->
          <div class="flex flex-col gap-6 text-sm font-medium">
            <div class="text-[#2d5396] text-lg font-medium">سند رسید</div>

            <div>مبلغ واریزی : <span class="ms-8"> - </span></div>
            <div>نوع پرداخت : <span class="ms-8"> - </span></div>
            <div>نوع انتقال : <span class="ms-8"> - </span></div>
            <div>شناسه یکتا : <span class="ms-8"> - </span></div>
            <div>تاریخ پرداخت : <span class="ms-8"> - </span></div>

            <div v-for="(img, index) in receipts" :key="`img-${index}`" class="-mt-2">
              <div class="w-full flex flex-col items-center lg:items-start">
                <div class="w-full gap-6 lg:w-fit flex items-center">
                  <img :src="img" class="w-full lg:w-[198px] h-[248px] object-cover rounded-lg" />

                  <Button
                    @click.stop="openImageInNewTab(img)"
                    :isAdmin="true"
                    class="w-fit px-5! rounded-lg h-10! py-0 text-[13px]"
                  >
                    <span class="flex items-center justify-center gap-2"> دانلود </span>
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <!-- Buyer Payments Section -->
          <div v-if="buyerPayment.length > 0" class="border-t border-[#eaeef5] pt-8">
            <div
              v-for="(factor, index) in buyerPayment"
              :key="`factor-${index}`"
              class="flex flex-col gap-6 text-sm font-medium"
            >
              <div class="text-[#2d5396] text-lg font-medium font-fa">فاکتور {{ index + 1 }}</div>

              <div>
                مبلغ واریزی :
                <span class="ms-8 font-fa">
                  {{ numberWithCommas(factor.amount) }}
                </span>
              </div>

              <div>
                نوع پرداخت :
                <span class="ms-8">
                  {{ factor.provider }}
                </span>
              </div>

              <div>
                نوع انتقال :
                <span class="ms-8">
                  {{ factor.transferType }}
                </span>
              </div>

              <div>
                شناسه یکتا :
                <span class="ms-8">
                  {{ factor.tracking_code }}
                </span>
              </div>

              <div>
                تاریخ پرداخت :
                <span dir="ltr" class="ms-8 font-fa">
                  {{ stringToFaDateTime(factor.statusUpdatedAt) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer Buttons -->
      <div
        class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200"
      >
        <Button
          @click="confirmSave"
          type="submit"
          :isAdmin="true"
          :disabled="confirmMutation.isPending.value"
          class="w-9/12 h-12 rounded-xl"
        >
          <span class="flex items-center justify-center gap-2"> تایید و ثبت پرداخت </span>
        </Button>

        <Button
          type="button"
          @click="closeModal"
          variant="outline"
          label="بازگشت"
          :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2"
        />
      </div>
    </div>
  </Modal>
</template>
